import {
  CORRIDOR_TARIFFS,
  formatInr,
  listPublishedFares,
} from './localCorridorFares';

const SITE_LINKS = [
  { path: '/coimbatore-to-ooty', label: 'Coimbatore to Ooty Taxi' },
  { path: '/coimbatore-to-chennai-taxi', label: 'Coimbatore to Chennai Taxi' },
  { path: '/singanallur-taxi', label: 'Singanallur Taxi' },
  { path: '/tariff', label: 'Tariff' },
  { path: '/fare-payment-policy', label: 'Fare & Payment Policy' },
];

const publishedFarePhrase = (tariff) => {
  const published = listPublishedFares(tariff);
  if (!published.length) return '';
  return published.map((item) => `${item.vehicleType} ${item.label}`).join(' and ');
};

const buildPage = (page) => ({
  ...page,
  displayOrigin: page.origin,
  displayDestination: page.destination,
  tariff: CORRIDOR_TARIFFS[page.tariffId],
});

const karurTariff = CORRIDOR_TARIFFS['coimbatore-karur'];
const pollachiTariff = CORRIDOR_TARIFFS['coimbatore-pollachi'];
const tiruppurTariff = CORRIDOR_TARIFFS['coimbatore-tiruppur'];
const karurFares = publishedFarePhrase(karurTariff);
const tiruppurFares = publishedFarePhrase(tiruppurTariff);
const pollachiFares = publishedFarePhrase(pollachiTariff);
const pollachiExtraKm = formatInr(pollachiTariff.additionalKmRate);

const pages = [
  buildPage({
    slug: '/coimbatore-to-karur',
    reverseSlug: '/karur-to-coimbatore',
    origin: 'Coimbatore',
    destination: 'Karur',
    direction: 'forward',
    tariffId: 'coimbatore-karur',
    h1: 'Coimbatore to Karur One Way Taxi',
    seoTitle: 'Coimbatore to Karur Taxi | One Way Package | Easy Drop Taxi',
    seoDescription: `Book a Coimbatore to Karur one-way taxi. Package fare: ${karurFares}, including ${karurTariff.includedDistanceKm} km. Extra distance is ${formatInr(karurTariff.additionalKmRate)} per km. Toll, parking and permit are included.`,
    heroLead: `Leave Coimbatore for Karur on a one-way package. The fare is ${karurFares}, including ${karurTariff.includedDistanceKm} km. Toll, parking and permit are included.`,
    sections: [
      {
        heading: 'Coimbatore to Karur taxi',
        paragraphs: [
          'Karur lies east of Coimbatore, on the way toward Trichy and the textile towns of central Tamil Nadu. Travellers book this one-way taxi for a mill visit, a family function, a hospital trip, or a rail connection when the cab does not need to stay for the return.',
          'The usual drive leaves Coimbatore toward Tiruppur and Kangeyam before entering Karur. Town traffic around Tiruppur, and the last stretch into Karur, can add time even when the distance sits near the kilometres covered by the published fare. If you have a train or a fixed meeting, leave a buffer rather than timing the trip to the minute.',
          `The one-way package is ${karurFares}. Each fare includes ${karurTariff.includedDistanceKm} km. Distance beyond that is ${formatInr(karurTariff.additionalKmRate)} per km. Toll, parking, and permit charges are included and are not added again.`,
          'The booking card starts with Coimbatore as pickup and Karur as drop. One Way Drop uses this package. Round Trip in the same card uses the website’s existing round-trip calculation and does not reuse this package price.',
        ],
      },
      {
        heading: 'The drive and what to plan',
        paragraphs: [
          'Give a full pickup address, not only the city name. Gandhipuram, Ukkadam, Coimbatore Junction, the airport, and Singanallur are common starts, and each one changes how long it takes to clear Coimbatore. For the drop, name the Karur bus stand, the railway station, a textile market, or the house you are actually visiting.',
          `The included ${karurTariff.includedDistanceKm} km is covered by the one-way package. A pickup on the western edge of Coimbatore, or a drop outside Karur town, can run longer. Those extra kilometres are ${formatInr(karurTariff.additionalKmRate)} each on a one-way trip.`,
          'Daytime departures are easier to predict than a late evening run through Tiruppur. If you are connecting to a Karur train, allow time for the city exit and for the station entrance you need. This booking is point to point. Extra stops are not part of the published fare unless you arrange them with support.',
          'One way means the trip ends at the Karur drop. You are not paying to send the same cab back empty unless you switch the form to Round Trip. When you later need the opposite journey, open the Karur to Coimbatore page so pickup and drop are set the right way around.',
          'Sedan is the one-way package for up to four people with ordinary luggage. SUV is the larger one-way cabin. Crysta is not offered for this one-way package. It remains available when you switch the booking card to Round Trip.',
        ],
      },
      {
        heading: 'Pickup and drop',
        paragraphs: [
          'Coimbatore pickup can be a house, a hotel, or the airport. Karur drop should name a landmark or a street. This website does not keep a separate neighbourhood catalogue for Karur, so the address you type is what the driver follows.',
          'If you are only passing through Karur toward Trichy or Dindigul, say so before you confirm. The published fare is for Coimbatore to Karur, not for a longer onward leg.',
          'You can still edit both fields in the booking card. On a one-way trip, distance above the included kilometres is charged at the package’s extra-kilometre rate. A round trip ignores that package and uses the normal website fare.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is the Coimbatore to Karur one-way taxi fare?',
        answer: `${karurFares}. Each one-way fare includes ${karurTariff.includedDistanceKm} km, plus ${formatInr(karurTariff.additionalKmRate)} per km after that. Toll, parking, and permit charges are included.`,
      },
      {
        question: 'Is the SUV fare the same as the sedan fare?',
        answer: `No. The one-way SUV package is ${formatInr(karurTariff.vehicles.SUV)}. The one-way sedan package is ${formatInr(karurTariff.vehicles.Sedan)}. Crysta is not offered on this one-way package. It remains available for Round Trip.`,
      },
      {
        question: 'How long does Coimbatore to Karur usually take?',
        answer: 'Many drives take roughly three hours, depending on Tiruppur traffic and the exact pickup and drop. This page does not guarantee a clock time.',
      },
      {
        question: 'Can I book only one way and not a return?',
        answer: 'Yes. The booking card on this page defaults to One Way, so the trip ends in Karur. Choose Round Trip in the same form if you need the cab for both legs.',
      },
      {
        question: 'Will the online estimate always show the published fare?',
        answer: 'On One Way Drop, the booking card uses this package. If the mapped trip is longer than the included distance, only the extra kilometres are added. Round Trip shows a different total because it uses the website’s normal round-trip fare.',
      },
      {
        question: 'Are tolls and driver allowance included?',
        answer: 'On One Way Drop, toll, parking, and permit charges are included in the package and are not billed again. Round Trip uses the website’s existing extra-charge rules instead of this package.',
      },
    ],
  }),
  buildPage({
    slug: '/karur-to-coimbatore',
    reverseSlug: '/coimbatore-to-karur',
    origin: 'Karur',
    destination: 'Coimbatore',
    direction: 'reverse',
    tariffId: 'coimbatore-karur',
    h1: 'Karur to Coimbatore One Way Taxi',
    seoTitle: 'Karur to Coimbatore Taxi | One Way Package | Easy Drop Taxi',
    seoDescription: `Book a Karur to Coimbatore one-way taxi. The same package applies: ${karurFares}, including ${karurTariff.includedDistanceKm} km. Extra distance is ${formatInr(karurTariff.additionalKmRate)} per km. Toll, parking and permit are included.`,
    heroLead: `Start in Karur and ride one way to Coimbatore. The package is ${karurFares}, including ${karurTariff.includedDistanceKm} km. Toll, parking and permit are included.`,
    sections: [
      {
        heading: 'Karur to Coimbatore taxi',
        paragraphs: [
          'The return from Karur to Coimbatore is its own one-way trip. People use it after a textile-market visit, a family event, or a train arrival in Karur when they want a door-to-door cab instead of changing buses at Tiruppur.',
          'You leave Karur toward Kangeyam and Tiruppur, then enter Coimbatore from the east. Evening traffic on the Tiruppur side and the approach into Gandhipuram, the airport, or Singanallur can stretch the last part of the journey. Build in time if you have a Coimbatore flight or train.',
          `The one-way package is the same price used toward Karur: ${karurFares}. It includes ${karurTariff.includedDistanceKm} km. Distance beyond that is ${formatInr(karurTariff.additionalKmRate)} per km. Toll, parking, and permit charges are included.`,
          'Pickup is prefilled as Karur and drop as Coimbatore. One Way Drop uses this package. Round Trip uses the website’s existing round-trip fare and does not reuse the one-way package.',
        ],
      },
      {
        heading: 'Leaving Karur',
        paragraphs: [
          'A useful Karur pickup is the railway station, the bus stand, or the exact street of your stay. “Karur” alone can leave the driver at the wrong side of town. In Coimbatore, name the drop you actually need: airport departures, Coimbatore Junction, Ukkadam, a hospital, or a house.',
          'Luggage from a market trip fits a sedan when it is a few bags. If you need a larger cabin, the one-way SUV package is the verified option. Crysta is not part of this one-way package. Switch to Round Trip if you need the vehicles the rest of the website offers for a return trip.',
          'One way ends when you reach the Coimbatore drop. The driver is not held for a later return unless you select Round Trip. For a later trip back to Karur, use the Coimbatore to Karur page instead of reversing the fields by memory.',
          'Tell support the train number only if you want them to plan the pickup. This page does not track rail delays. The one-way package includes toll, parking, and permit charges, and it does not add those amounts a second time.',
          'The road is a plain intercity run, not a hill crossing. You should still expect speed breakers and town limits at Kangeyam and on the edge of Tiruppur. Those slow sections are normal and are not a reason to invent a different fare.',
        ],
      },
      {
        heading: 'Pickup and drop',
        paragraphs: [
          'Karur is not in the homepage city shortcut list as a neighbourhood guide. Type the pickup you want, and use Places suggestions if they appear. Coimbatore is a known service city, and you can still replace the drop with a specific address.',
          'Airport drops need the terminal you are using. On a one-way package, parking is included and is not added again. A round trip follows the website’s normal extra-charge rules instead.',
          'If your drop is beyond Coimbatore, such as onward to Ooty or Pollachi, that is a different route with its own page. Do not extend this Karur fare to cover that second leg.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is the Karur to Coimbatore one-way fare?',
        answer: `${karurFares}. The one-way package includes ${karurTariff.includedDistanceKm} km, then ${formatInr(karurTariff.additionalKmRate)} per extra km. Toll, parking, and permit charges are included.`,
      },
      {
        question: 'Is the return priced differently from Coimbatore to Karur?',
        answer: `No. Both directions use the same one-way sedan and SUV packages. Extra distance is ${formatInr(karurTariff.additionalKmRate)} per km after ${karurTariff.includedDistanceKm} km. Round Trip does not use this package.`,
      },
      {
        question: 'Can you pick me up from Karur railway station?',
        answer: 'Yes. Replace the pickup field with Karur railway station, or the exit you will use, before you continue the booking. The city name alone is only the starting text.',
      },
      {
        question: 'How do I reach Coimbatore airport from Karur?',
        answer: 'Set the drop to Coimbatore airport and keep One Way selected if you are flying out. Parking is included in the one-way package and is not charged again.',
      },
      {
        question: 'Why might the booking form show another amount?',
        answer: `One Way Drop shows the package, plus ${formatInr(karurTariff.additionalKmRate)} per km only when the mapped distance is over the included kilometres. Round Trip shows the website’s normal round-trip total, which is not the one-way package.`,
      },
      {
        question: 'Is an Crysta fare available?',
        answer: 'Crysta is not offered for this one-way package. Sedan and SUV are the one-way choices. Round Trip still uses the website’s normal vehicle list.',
      },
    ],
  }),
  buildPage({
    slug: '/coimbatore-to-pollachi',
    reverseSlug: '/pollachi-to-coimbatore',
    origin: 'Coimbatore',
    destination: 'Pollachi',
    direction: 'forward',
    tariffId: 'coimbatore-pollachi',
    h1: 'Coimbatore to Pollachi One Way Taxi',
    seoTitle: 'Coimbatore to Pollachi Taxi | One Way Package | Easy Drop Taxi',
    seoDescription: `Book a Coimbatore to Pollachi one-way taxi. ${pollachiFares}, including ${pollachiTariff.includedDistanceKm} km. Extra distance is ${pollachiExtraKm} per km. Toll and parking are extra.`,
    heroLead: `Book a one-way taxi from Coimbatore to Pollachi. The package is ${pollachiFares}, including ${pollachiTariff.includedDistanceKm} km. Toll and parking are extra.`,
    sections: [
      {
        heading: 'Coimbatore to Pollachi taxi',
        paragraphs: [
          'Pollachi is the southern town Coimbatore uses as the gateway toward Aliyar, Valparai, and the Anamalai side. A one-way taxi makes sense when you are going to Pollachi town itself and you do not want the cab held overnight.',
          'The road south from Coimbatore is shorter than the Karur run. Kinathukadavu and the approach into Pollachi are the usual slow points, especially in the morning and late afternoon.',
          `The one-way package is ${pollachiFares}. Each fare includes ${pollachiTariff.includedDistanceKm} km. Distance beyond that is ${pollachiExtraKm} per km. Toll and parking are extra and are not added as a second amount on this page.`,
          'The booking card starts with Coimbatore as pickup and Pollachi as drop. One Way Drop uses this package. Round Trip in the same card uses the website’s existing round-trip calculation and does not reuse these one-way fares.',
        ],
      },
      {
        heading: 'Distance, extra kilometres, and the hills',
        paragraphs: [
          `The included ${pollachiTariff.includedDistanceKm} km is the coverage inside the package, not a fixed trip length. A pickup at the airport or in Singanallur and a drop at Pollachi bus stand is measured by the same distance used in the booking form. A house on the far side of Pollachi, or a start in north Coimbatore, can run longer. Those extra kilometres are ${pollachiExtraKm} each on a one-way trip.`,
          'Pollachi town and the hill road to Valparai are not the same trip. This package is for Coimbatore to Pollachi. Continuing to Valparai, Topslip, or Aliyar dam is a different drop and is not covered by the included kilometres. Tell support if that is the real destination before you confirm.',
          'Sedan is the one-way package for up to four people with ordinary luggage. SUV is the larger one-way cabin. Crysta is not offered for this one-way package. It remains available when you switch the booking card to Round Trip.',
          'One way ends in Pollachi. Switch the form to Round Trip only if the same cab must bring you back. Otherwise book the return later from the Pollachi to Coimbatore page. That page uses the same one-way package, with pickup and drop swapped.',
        ],
      },
      {
        heading: 'Pickup and drop',
        paragraphs: [
          'In Coimbatore, common pickups are Gandhipuram, Ukkadam, the railway stations, the airport, and Singanallur. Each adds a different city-exit time. In Pollachi, name the bus stand, a college, a hotel, or the street.',
          'You can still edit both fields in the booking card. On a one-way trip, distance above the included kilometres is charged at the package’s extra-kilometre rate. Toll and parking stay extra. A round trip leaves this package and uses the normal website fare.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is the Coimbatore to Pollachi one-way taxi fare?',
        answer: `${pollachiFares}. Each one-way fare includes ${pollachiTariff.includedDistanceKm} km, plus ${pollachiExtraKm} per km after that. Toll and parking are extra.`,
      },
      {
        question: 'Is the SUV fare the same as the sedan fare?',
        answer: `No. The one-way SUV package is ${formatInr(pollachiTariff.vehicles.SUV)}. The one-way sedan package is ${formatInr(pollachiTariff.vehicles.Sedan)}. Crysta is not offered on this one-way package. It remains available for Round Trip.`,
      },
      {
        question: 'Does this fare include Valparai or Aliyar?',
        answer: `No. The package covers the ride to Pollachi, including ${pollachiTariff.includedDistanceKm} km. Hill destinations beyond Pollachi are a different trip. Extra kilometres on this corridor are ${pollachiExtraKm} per km, and a hill continuation should be confirmed with support.`,
      },
      {
        question: 'How long is the drive?',
        answer: 'Town-to-town trips are often about an hour to an hour and a half, depending on where you start in Coimbatore and traffic at Kinathukadavu. No travel time is guaranteed.',
      },
      {
        question: 'Are tolls included in the package?',
        answer: 'No. Toll and parking are extra and are not included in the sedan or SUV package. The booking summary does not add a second toll or parking amount.',
      },
      {
        question: 'Does Round Trip use this package?',
        answer: 'No. Round Trip uses the website’s existing round-trip fare, distance, and vehicle list. Switching back to One Way restores this package.',
      },
    ],
  }),
  buildPage({
    slug: '/pollachi-to-coimbatore',
    reverseSlug: '/coimbatore-to-pollachi',
    origin: 'Pollachi',
    destination: 'Coimbatore',
    direction: 'reverse',
    tariffId: 'coimbatore-pollachi',
    h1: 'Pollachi to Coimbatore One Way Taxi',
    seoTitle: 'Pollachi to Coimbatore Taxi | One Way Package | Easy Drop Taxi',
    seoDescription: `Book a Pollachi to Coimbatore one-way taxi. ${pollachiFares}, including ${pollachiTariff.includedDistanceKm} km. Extra distance is ${pollachiExtraKm} per km. Toll and parking are extra.`,
    heroLead: `Ride one way from Pollachi to Coimbatore. The package is ${pollachiFares}, including ${pollachiTariff.includedDistanceKm} km. Toll and parking are extra.`,
    sections: [
      {
        heading: 'Pollachi to Coimbatore taxi',
        paragraphs: [
          'Leaving Pollachi for Coimbatore is the short northbound one-way trip: after a market visit, a college day, a hospital appointment, or the end of a stay before you continue by air or train from Coimbatore.',
          'The road runs back through Kinathukadavu into the south side of Coimbatore. Airport and Singanallur drops meet that approach directly. A drop in Gandhipuram or at Coimbatore Junction continues through the city and can take longer than the open-road portion.',
          `The one-way package is the same in this direction: ${pollachiFares}. Each fare includes ${pollachiTariff.includedDistanceKm} km. Distance beyond that is ${pollachiExtraKm} per km. Toll and parking are extra and are not added as a second amount on this page.`,
          'The booking card opens with Pollachi as pickup and Coimbatore as drop. One Way Drop uses this package. Round Trip in the same card uses the website’s existing round-trip calculation and does not reuse these one-way fares.',
        ],
      },
      {
        heading: 'From Pollachi town to a Coimbatore address',
        paragraphs: [
          'Set the pickup to the Pollachi place you are actually leaving: the bus stand, a lodge, a college gate, or a house. “Pollachi” is only the default label. For the drop, write the Coimbatore landmark. Airport travellers should name the terminal so the driver does not stop at the wrong forecourt.',
          `The included ${pollachiTariff.includedDistanceKm} km is package coverage, not a substitute for the mapped distance. A simple town-to-town ride often stays inside it. Starting from a village south of Pollachi, or ending past Coimbatore, can run longer. Those extra kilometres are ${pollachiExtraKm} each on a one-way trip.`,
          'If you came down from Valparai or Aliyar and only need the car from Pollachi onward, book this page for the Pollachi–Coimbatore leg. The package does not include the hill road.',
          'Sedan is the one-way package for up to four people with ordinary luggage. SUV is the larger one-way cabin. Crysta is not offered for this one-way package. It remains available when you switch the booking card to Round Trip.',
        ],
      },
      {
        heading: 'Pickup and drop',
        paragraphs: [
          'Pollachi pickups are easiest when you share a nearby landmark. Coimbatore drops that people use most are the airport, Coimbatore Junction, Ukkadam, Gandhipuram, and Singanallur.',
          'You can still edit both fields. On a one-way trip, distance above the included kilometres is charged at the package extra-kilometre rate. Toll and parking stay extra. Switching to Round Trip leaves this package.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is the Pollachi to Coimbatore one-way taxi fare?',
        answer: `${pollachiFares}. Each one-way fare includes ${pollachiTariff.includedDistanceKm} km, plus ${pollachiExtraKm} per km after that. Toll and parking are extra.`,
      },
      {
        question: 'Does the return cost more than Coimbatore to Pollachi?',
        answer: `No. Both directions use ${pollachiFares}, the same ${pollachiTariff.includedDistanceKm} km included, and the same ${pollachiExtraKm} extra-kilometre rate.`,
      },
      {
        question: 'Is the SUV fare the same as the sedan fare?',
        answer: `No. The one-way SUV package is ${formatInr(pollachiTariff.vehicles.SUV)}. The one-way sedan package is ${formatInr(pollachiTariff.vehicles.Sedan)}. Crysta is not offered on this one-way package. It remains available for Round Trip.`,
      },
      {
        question: 'Will you pick up outside Pollachi town?',
        answer: `Yes, if you type that pickup. Distance beyond the included ${pollachiTariff.includedDistanceKm} km is charged at ${pollachiExtraKm} per km.`,
      },
      {
        question: 'Is the airport drop included?',
        answer: 'The ride to Coimbatore airport uses this one-way package. Distance above the included kilometres is charged at the extra-kilometre rate. Airport parking, if levied, is extra and is not added on this page.',
      },
      {
        question: 'Does Round Trip use this package?',
        answer: 'No. Round Trip uses the website’s existing round-trip fare, distance, and vehicle list. Switching back to One Way restores this package.',
      },
    ],
  }),
  buildPage({
    slug: '/coimbatore-to-tiruppur',
    reverseSlug: '/tiruppur-to-coimbatore',
    origin: 'Coimbatore',
    destination: 'Tiruppur',
    direction: 'forward',
    tariffId: 'coimbatore-tiruppur',
    h1: 'Coimbatore to Tiruppur One Way Taxi',
    seoTitle: 'Coimbatore to Tiruppur Taxi | One Way Package | Easy Drop Taxi',
    seoDescription: `Book a Coimbatore to Tiruppur one-way taxi. ${tiruppurFares}, including ${tiruppurTariff.includedDistanceKm} km. Extra distance is ${formatInr(tiruppurTariff.additionalKmRate)} per km. Toll and parking are extra.`,
    heroLead: `Book a one-way taxi from Coimbatore to Tiruppur. The package is ${tiruppurFares}, including ${tiruppurTariff.includedDistanceKm} km. Toll and parking are extra.`,
    sections: [
      {
        heading: 'Coimbatore to Tiruppur taxi',
        paragraphs: [
          'Tiruppur is the knitwear city immediately east of Coimbatore. Buyers, factory staff, and families travel between the two cities all day, often for a meeting that ends without a return cab. A one-way taxi fits that pattern: you ride to Tiruppur and the trip stops there.',
          'The road follows the Avinashi side out of Coimbatore, through the industrial edge, and into Tiruppur. It is a short intercity run, but shift-change traffic and lorries near the textile units can make the same kilometres take very different times. Leave earlier when you have a buyer appointment or a train at Tiruppur.',
          `The one-way package is ${tiruppurFares}. Each fare includes ${tiruppurTariff.includedDistanceKm} km. Distance beyond that is ${formatInr(tiruppurTariff.additionalKmRate)} per km. Toll and parking are extra and are not added as a second amount on this page.`,
          'The booking card starts with Coimbatore as pickup and Tiruppur as drop. One Way Drop uses this package. Round Trip in the same card uses the website’s existing round-trip calculation and does not reuse this package price.',
        ],
      },
      {
        heading: 'The drive and what to plan',
        paragraphs: [
          'Type a real pickup. Avinashi Road, Gandhipuram, the airport, Peelamedu, and Singanallur each start the clock in a different place. For the drop, use the Tiruppur railway station, the bus stand, a specific factory, or the market street. “Tiruppur” alone is a label, not an entrance.',
          `The included ${tiruppurTariff.includedDistanceKm} km is covered by the one-way package. A pickup on the far side of Coimbatore, or a factory gate outside Tiruppur town, can run longer. Those extra kilometres are ${formatInr(tiruppurTariff.additionalKmRate)} each on a one-way trip.`,
          'Sedan is the one-way package for up to four people with ordinary luggage. SUV is the larger one-way cabin. Crysta is not offered for this one-way package. It remains available when you switch the booking card to Round Trip.',
          'One way is the default. Choose Round Trip in the same form if the cab must wait and bring you back to Coimbatore. A later return, on another day, belongs on the Tiruppur to Coimbatore page.',
          'This corridor often shares the road with the longer Karur journey, but it is not the Karur fare. Do not apply the Karur sedan or SUV price to a trip that ends in Tiruppur.',
        ],
      },
      {
        heading: 'Pickup and drop',
        paragraphs: [
          'Coimbatore pickup can be a house, a hotel, or the airport. Tiruppur drop should name the unit, the lodge, or the station. Factory gates on the ring outside town are a common reason the pin and the city name disagree, so write the gate.',
          'If you need several buyer stops, that is more than a single drop. The standard form is one pickup and one drop. Ask support before you add stops, because extra halts are not part of the package.',
          'You can still edit both fields in the booking card. On a one-way trip, distance above the included kilometres is charged at the package’s extra-kilometre rate. Toll and parking stay extra. A round trip ignores this package and uses the normal website fare.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is the Coimbatore to Tiruppur one-way taxi fare?',
        answer: `${tiruppurFares}. Each one-way fare includes ${tiruppurTariff.includedDistanceKm} km, plus ${formatInr(tiruppurTariff.additionalKmRate)} per km after that. Toll and parking are extra.`,
      },
      {
        question: 'Is the SUV fare the same as the sedan fare?',
        answer: `No. The one-way SUV package is ${formatInr(tiruppurTariff.vehicles.SUV)}. The one-way sedan package is ${formatInr(tiruppurTariff.vehicles.Sedan)}. Crysta is not offered on this one-way package. It remains available for Round Trip.`,
      },
      {
        question: 'Can I book a one-way cab for a factory visit?',
        answer: 'Yes. Set the Tiruppur drop to the factory gate, keep One Way selected, and complete the usual booking steps. Extra stops need to be agreed with support.',
      },
      {
        question: 'How long does Coimbatore to Tiruppur take?',
        answer: 'Many trips take about an hour to an hour and a half, depending on Avinashi Road traffic and the exact gates you use. The time is not guaranteed.',
      },
      {
        question: 'Are tolls included in the package?',
        answer: 'No. Toll and parking are extra and are not included in the sedan or SUV package. The booking summary does not add a second toll or parking amount.',
      },
      {
        question: 'Does Round Trip use this package?',
        answer: 'No. Round Trip uses the website’s existing round-trip fare, distance, and vehicle list. Switching back to One Way restores this package.',
      },
    ],
  }),
  buildPage({
    slug: '/tiruppur-to-coimbatore',
    reverseSlug: '/coimbatore-to-tiruppur',
    origin: 'Tiruppur',
    destination: 'Coimbatore',
    direction: 'reverse',
    tariffId: 'coimbatore-tiruppur',
    h1: 'Tiruppur to Coimbatore One Way Taxi',
    seoTitle: 'Tiruppur to Coimbatore Taxi | One Way Package | Easy Drop Taxi',
    seoDescription: `Book a Tiruppur to Coimbatore one-way taxi. ${tiruppurFares}, including ${tiruppurTariff.includedDistanceKm} km. Extra distance is ${formatInr(tiruppurTariff.additionalKmRate)} per km. Toll and parking are extra.`,
    heroLead: `Leave Tiruppur for Coimbatore in a one-way taxi. The package is ${tiruppurFares}, including ${tiruppurTariff.includedDistanceKm} km. Toll and parking are extra.`,
    sections: [
      {
        heading: 'Tiruppur to Coimbatore taxi',
        paragraphs: [
          'The westbound ride from Tiruppur to Coimbatore is how buyers return after a knitwear visit, how families reach Coimbatore hospitals, and how travellers connect to a flight or a train. Booking one way means you do not keep a cab in Tiruppur after you have left.',
          'You join the Avinashi corridor and enter Coimbatore from the east. Airport and Peelamedu drops come up before the older city centre. Gandhipuram, Ukkadam, and Coimbatore Junction take you further in, so the same departure can arrive at very different times.',
          `The one-way package is the same in this direction: ${tiruppurFares}. Each fare includes ${tiruppurTariff.includedDistanceKm} km. Distance beyond that is ${formatInr(tiruppurTariff.additionalKmRate)} per km. Toll and parking are extra and are not added as a second amount on this page.`,
          'The booking card opens with Tiruppur as pickup and Coimbatore as drop. One Way Drop uses this package. Round Trip in the same card uses the website’s existing round-trip calculation and does not reuse this package price.',
        ],
      },
      {
        heading: 'Station, airport, and factory pickups',
        paragraphs: [
          'A Tiruppur railway station pickup should name the exit you will use. A factory pickup should name the company and the gate, because industrial estates repeat similar road names. The default word “Tiruppur” is only there so you do not start from a blank field.',
          'For a flight, set the drop to Coimbatore airport and leave room for a security queue after you arrive. The taxi fare is for the ride. It is not an air ticket, and airport parking is extra if a paid wait is required.',
          `The included ${tiruppurTariff.includedDistanceKm} km is package coverage, not a fixed trip length. A factory gate outside Tiruppur, or a drop on the far side of Coimbatore, can run longer. Those extra kilometres are ${formatInr(tiruppurTariff.additionalKmRate)} each on a one-way trip, for both Sedan and SUV.`,
          'Sedan is the one-way package for up to four people with ordinary luggage. SUV is the larger one-way cabin. Crysta is not offered for this one-way package. It remains available when you switch the booking card to Round Trip.',
          'If your trip continues past Coimbatore to Pollachi or Ooty, stop this booking at Coimbatore only when that is the real drop. Pollachi and Ooty have their own route pages. One way ends at the Coimbatore address you enter. Round Trip, in the same form, is the switch for a same-day return to Tiruppur.',
        ],
      },
      {
        heading: 'Pickup and drop',
        paragraphs: [
          'Edit both fields before you continue. A precise Tiruppur pickup and a precise Coimbatore drop are what the included kilometres are measured against.',
          'You can still edit both fields in the booking card. On a one-way trip, distance above the included kilometres is charged at the package’s extra-kilometre rate. Toll and parking stay extra. A round trip ignores this package and uses the normal website fare.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is the Tiruppur to Coimbatore one-way taxi fare?',
        answer: `${tiruppurFares}. Each one-way fare includes ${tiruppurTariff.includedDistanceKm} km, plus ${formatInr(tiruppurTariff.additionalKmRate)} per km after that. Toll and parking are extra.`,
      },
      {
        question: 'Does the return cost more than Coimbatore to Tiruppur?',
        answer: `No. Both directions use ${tiruppurFares}, the same ${tiruppurTariff.includedDistanceKm} km included, and the same ${formatInr(tiruppurTariff.additionalKmRate)} extra-kilometre rate.`,
      },
      {
        question: 'Is the SUV fare the same as the sedan fare?',
        answer: `No. The one-way SUV package is ${formatInr(tiruppurTariff.vehicles.SUV)}. The one-way sedan package is ${formatInr(tiruppurTariff.vehicles.Sedan)}. Crysta is not offered on this one-way package. It remains available for Round Trip.`,
      },
      {
        question: 'Can the cab pick me up at Tiruppur railway station?',
        answer: 'Yes. Change the pickup to Tiruppur railway station and the exit you will use, then continue in the booking form.',
      },
      {
        question: 'Can I go straight to Coimbatore airport?',
        answer: 'Yes. Set that as the drop. The ride uses this one-way package. Airport parking, if charged, is extra and is not added on this page.',
      },
      {
        question: 'Does Round Trip use this package?',
        answer: 'No. Round Trip uses the website’s existing round-trip fare, distance, and vehicle list. Switching back to One Way restores this package.',
      },
    ],
  }),
];

pages.forEach((page) => {
  const siblings = pages
    .filter((other) => other.slug !== page.slug)
    .map((other) => ({ path: other.slug, label: other.h1 }));
  page.related = [...siblings, ...SITE_LINKS];
});

export const LOCAL_CORRIDOR_PAGES = pages;

export const getLocalCorridorPage = (slug) => (
  LOCAL_CORRIDOR_PAGES.find((page) => page.slug === slug) || null
);
